import * as CL from '@design-system/component-library';
import * as React from 'react';
import { type JSX, useRef, useState } from 'react';
import { dsClass } from '../../common/constants/dsClasses';
import { ModelType } from '../../common/enums';
import { closeMsg, contractPriceCapitalizedMsg, t } from '../../common/i18n';
import { clampQuantity } from '../../common/utils/quantityUtils';
import { OnlineModelCategory } from '../../generated/api/onlineModelCategory';
import { useAuth } from '../../public/site/AuthProvider';
import { sendSelectionEventForPlaceOrder } from '../PlaceOrder/utils/placeOrderAnalyticsUtils';
import { ProductVariantSelection } from './ProductVariantSelection';
import { SubscriptionModal } from './SubscriptionModal';
import { Price, getSaving, hasCampaignDiscount, hasDiscount, isMobileView } from './SubscriptionUtils';
import type { ModalDescriptionItem } from './SubscriptionModal';
import type { SubCardWithAnalyticsV4Props } from './subscriptionCardsAnalyticsUtils';
import type { OrderProps, PublicPageOrderProps, SubscriptionCardWithModel } from './types';
import type { CLPopoverImperativeRefHandle } from '../../common/types/componentLibrary';
import type { MobileBroadbandSubCardProps } from '../SubscriptionCard/SubscriptionCard';

import './SubscriptionCard.scss';

export interface SubscriptionCardProps {
  modal?: JSX.Element;
  badges: JSX.Element[];
  subscriptionCard?: SubscriptionCardWithModel;
  subHeader: string;
  header: string;
  description: string | JSX.Element;
  infoItems: InfoItem[];
  hasQuantity: boolean;
  buttonText: string;
  onOrder: (p: PublicPageOrderProps | OrderProps) => void;
  additionalFeatures?: InfoItem[];
  modalDescriptionItems?: ModalDescriptionItem[];
  analyticsListName?: string;
  analyticsIndex: number;
  isChangeOffer: boolean;
  buttonMakesOrder?: boolean;
  showFixedTermInfo?: boolean;
  hideNumberSelection?: boolean;
  showAddonConfiguration?: boolean;
  onCardClick?: (card: SubscriptionCardWithModel, modalDescriptionItems?: ModalDescriptionItem[]) => void;
  showAlternateProductChoice?: boolean;
  productVariantInfoBullets?: InfoItem[];
  onSelectOffer?: (guid: string, name: string) => void;
  compact?: boolean;
}

export interface InfoItem {
  icon: string;
  text: string;
  popoverContent?: JSX.Element;
}

interface PopOverProps {
  content?: JSX.Element;
  activePopoverRef: React.MutableRefObject<CLPopoverImperativeRefHandle | null>;
}

export const HorizontalCardPopOver = ({ content, activePopoverRef }: PopOverProps) => {
  const popoverRef = useRef<CLPopoverImperativeRefHandle>(null);
  const scrollPositionRef = useRef<number>(0);

  return content ? (
    <CL.Popover
      componentRef={popoverRef}
      triggerElement={
        <CL.Icon
          className="of-info-popover-trigger"
          color="blue-600"
          icon="information"
          size="s"
          type="regular"
          onClick={() => {
            scrollPositionRef.current = window.scrollY;
          }}
        />
      }
      placement="right"
      defaultOpen={false}
      i18n_popover_closeButtonTitle={t.WOYD(closeMsg)}
      onOpen={() => {
        if (activePopoverRef.current && activePopoverRef.current !== popoverRef.current) {
          activePopoverRef.current.closePopover();
        }
        activePopoverRef.current = popoverRef.current;

        // This is to prevent the page from scrolling to top when the popover is opened, Popover that doesn't have an option to disable the annoying page jump
        setTimeout(() => {
          window.scrollTo(0, scrollPositionRef.current);
        }, 0);
      }}
      onClose={() => {
        activePopoverRef.current = null;
      }}
    >
      {content}
    </CL.Popover>
  ) : null;
};

export const SubscriptionCard = ({
  badges,
  subHeader,
  header,
  description,
  infoItems,
  hasQuantity,
  buttonText,
  additionalFeatures,
  subscriptionCard,
  modalDescriptionItems,
  onOrder,
  analyticsListName,
  analyticsIndex,
  isChangeOffer,
  buttonMakesOrder,
  showFixedTermInfo = false,
  hideNumberSelection = false,
  showAddonConfiguration = false,
  onCardClick,
  showAlternateProductChoice,
  productVariantInfoBullets,
  onSelectOffer,
  compact = false,
}: SubscriptionCardProps) => {
  const [showModal, setShowModal] = React.useState(false);
  const onlineModel = subscriptionCard?.onlineModel;
  const { isAuthenticated } = useAuth();
  const [quantity, setQuantity] = useState<number>(1);
  const [newOrKeepNumber, setNewOrKeepNumber] = useState('newNumber');

  const activePopoverRef = useRef<CLPopoverImperativeRefHandle | null>(null);

  const getVoucherBadge = () => {
    if (subscriptionCard?.voucherCode) {
      return <CL.Badge key="voucher" color="discount" text={subscriptionCard.voucherCode} />;
    }
    return undefined;
  };

  const hasOfferBadgeFromParent = badges.some(badge => {
    if (!React.isValidElement(badge)) {
      return false;
    }
    return badge.key === 'offer';
  });

  const getCampaignBadge = () => {
    if (!hasOfferBadgeFromParent && !subscriptionCard?.voucherCode && hasCampaignDiscount(subscriptionCard)) {
      return <CL.Badge key="offer" color="discount" text={t.TJPQ('Offer')} type="text" />;
    }
    return undefined;
  };

  const shouldContractPriceBadgeBeShown = () =>
    !subscriptionCard?.voucherCode &&
    !hasCampaignDiscount(subscriptionCard) &&
    isAuthenticated &&
    hasDiscount(subscriptionCard);

  const getContractPriceBadge = () => {
    if (shouldContractPriceBadgeBeShown()) {
      return <CL.Badge key="contract-price" color="discount" text={t.JPKP(contractPriceCapitalizedMsg)} />;
    }
    return undefined;
  };

  // Filter out falsy (undefined from above) values
  const allBadges = [...badges, getVoucherBadge(), getCampaignBadge(), getContractPriceBadge()].filter(Boolean);

  const marginByBadgesLength = () => {
    const mobileMargin = isMobileView ? dsClass.MARGIN_TOP_5 : ``;
    return allBadges.length !== 0 ? dsClass.MARGIN_TOP_3 : mobileMargin;
  };

  const InfoItemsList = () => (
    <div className="of-subscription-card-content-info-items">
      {infoItems.map(infoItem => (
        <div
          key={infoItem.text}
          className={`${dsClass.MARGIN_VERTICAL_2} ${dsClass.DISPLAY_FLEX} ${dsClass.ALIGN_ITEMS_CENTER}`}
        >
          <CL.Icon className="of-info-icon ds-margin-right--2" icon={infoItem.icon} size="s" />
          <span className={`of-info-item-text ${dsClass.TEXT_S}`}>
            {infoItem.text}
            <HorizontalCardPopOver activePopoverRef={activePopoverRef} content={infoItem.popoverContent} />
          </span>
        </div>
      ))}
    </div>
  );

  const AdditionalFeaturesList = () => (
    <ul className={`of-two-column-flex ${dsClass.MARGIN_TOP_3}`}>
      {additionalFeatures?.map(feature => (
        <li key={feature.text}>
          <CL.Icon className="of-info-icon" size="s" icon={feature.icon} />
          <span className={`of-info-item-text ${dsClass.TEXT_S}`}>
            {feature.text}
            <HorizontalCardPopOver activePopoverRef={activePopoverRef} content={feature.popoverContent} />
          </span>
        </li>
      ))}
    </ul>
  );

  const Modal = () =>
    onlineModel ? (
      <SubscriptionModal
        header={header}
        onOrder={onOrder}
        descriptionItems={modalDescriptionItems || []}
        offers={(subscriptionCard as SubCardWithAnalyticsV4Props).offers}
        newOrKeepNumber={hideNumberSelection ? undefined : newOrKeepNumber}
        onNumberSelectionChange={hideNumberSelection ? undefined : (n: string) => setNewOrKeepNumber(n)}
        onCloseDialog={() => {
          setShowModal(false);
        }}
        onlineModel={onlineModel}
        subscriptionCard={subscriptionCard}
        hasQuantity={!isChangeOffer}
        isChangeOffer={isChangeOffer}
        showAlternateProductChoice={compact && showAlternateProductChoice}
        onSelectOffer={onSelectOffer}
        showNewOrKeepNumber={onlineModel.onlineModelCode !== ModelType.YrityspuheUusi}
        showAddonConfiguration={showAddonConfiguration}
        commercialProduct={(subscriptionCard as SubCardWithAnalyticsV4Props)?.offer?.commercialProducts?.[0]}
      />
    ) : null;

  const Accordion = () => (
    <div className="of-subscription-card-content-additional-features">
      {additionalFeatures && (
        <CL.Accordion className={`${dsClass.MARGIN_TOP_2}`}>
          <CL.AccordionItem
            heading={
              <h5
                className={dsClass.MARGIN_0}
              >{`${t.N2A3('Services included in the subscription')} (${additionalFeatures.length})`}</h5>
            }
          >
            <AdditionalFeaturesList />
          </CL.AccordionItem>
        </CL.Accordion>
      )}
    </div>
  );

  const handleOrderClick = () => {
    if (onlineModel) {
      const subCardAnalytics = subscriptionCard as SubCardWithAnalyticsV4Props;
      if (!subCardAnalytics.offer) {
        if (onlineModel.category === OnlineModelCategory.SALES_PRODUCT) {
          const offerCode = (subscriptionCard as MobileBroadbandSubCardProps)?.publicPage?.offerCode;
          const foundOffer = subCardAnalytics.onlineModel.offers.find(offer => offer.offerCode === offerCode);
          if (foundOffer) {
            subCardAnalytics.offer = foundOffer;
          }
        }
        if (!subCardAnalytics.offer) {
          subCardAnalytics.offer = subCardAnalytics.onlineModel.offers[0];
        }
      }
      sendSelectionEventForPlaceOrder(subCardAnalytics, analyticsIndex, analyticsListName);
    }
    if (buttonMakesOrder && onlineModel) {
      const card = subscriptionCard as MobileBroadbandSubCardProps;
      const subCardAnalytics = subscriptionCard as SubCardWithAnalyticsV4Props;

      onOrder({
        isAuthenticated: isAuthenticated,
        onlineModel: onlineModel,
        quantity: quantity,
        isNewNumber: true,
        laitenettiOfferName: subCardAnalytics.offer?.offerName,
        noeOfferCode: subCardAnalytics.selectedOffer,
        voucherCode: subscriptionCard?.voucherCode,
        offerCode: card?.publicPage?.offerCode,
        addonCode: card?.publicPage?.addonCode,
        bbOfferCode: subCardAnalytics.offer?.offerCode,
        services: subscriptionCard?.services,
      });
    }
    if (!buttonMakesOrder) {
      if (onCardClick && subscriptionCard) {
        onCardClick(subscriptionCard, modalDescriptionItems);
      } else {
        setShowModal(true);
      }
    }
  };

  const CardPriceAndAction = () => (
    <div className="of-subscription-card-content-flex-item of-price-content">
      <div className={`of-subscription-card-content-price ${dsClass.MARGIN_BOTTOM_1}`}>
        <Price
          onetime={isChangeOffer ? subscriptionCard?.serviceFee : subscriptionCard?.oneTimePrice}
          monthly={subscriptionCard?.monthlyPrice}
          inModal={false}
          quantity={quantity}
          pricePreamble={subscriptionCard?.pricePreamble}
          saving={getSaving(subscriptionCard, quantity)}
          isChangeOffer={isChangeOffer}
          showFixedTermInfo={showFixedTermInfo}
          fixedTermMonths={subscriptionCard?.fixedTermMonths}
        />
      </div>
      <div className={`of-subscription-card-content-button ${!hasQuantity ? 'without-quantity' : ''}`}>
        {hasQuantity && (
          <CL.Quantity
            className={`of-subscription-quantity ${dsClass.MARGIN_RIGHT_2}`}
            minValue={1}
            currentValue={quantity}
            onChange={(q: number) => setQuantity(clampQuantity(q))}
          />
        )}
        <CL.Button size="l" block={hasQuantity ? undefined : true} onClick={handleOrderClick}>
          {buttonText}
        </CL.Button>
      </div>
    </div>
  );

  const isNewVoiceSubscription = subscriptionCard?.onlineModel.onlineModelCode === ModelType.YrityspuheUusi;

  if (compact) {
    return (
      <>
        {showModal ? <Modal /> : null}
        <div className={`of-subscription-card-container ${dsClass.BACKGROUND_COLOR_WHITE}`}>
          <div
            className={`of-subscription-card-content of-subscription-card-content--compact ${dsClass.BORDER_WIDTH_1} ${dsClass.BORDER_STYLE_SOLID}`}
          >
            <div className="of-subscription-card-content-badges">{allBadges}</div>
            <div className="of-subscription-card-content-flex-container">
              <div className="of-subscription-card-content-flex-item of-main-content">
                <div className="of-subscription-card-content-header">
                  {!compact && <span>{subHeader}</span>}
                  <h3>{header}</h3>
                </div>
                <div className="of-subscription-card-content-description">
                  <span className={dsClass.TEXT_M}>{description}</span>
                </div>
              </div>
              <div className="of-subscription-card-content-flex-item of-price-content">
                <div className={`of-subscription-card-content-price ${dsClass.MARGIN_BOTTOM_1}`}>
                  <Price
                    onetime={isChangeOffer ? subscriptionCard?.serviceFee : subscriptionCard?.oneTimePrice}
                    monthly={subscriptionCard?.monthlyPrice}
                    inModal={false}
                    quantity={quantity}
                    pricePreamble={subscriptionCard?.pricePreamble}
                    saving={getSaving(subscriptionCard, quantity)}
                    isChangeOffer={isChangeOffer}
                    showFixedTermInfo={showFixedTermInfo}
                    fixedTermMonths={subscriptionCard?.fixedTermMonths}
                  />
                </div>
                <div className="of-subscription-card-content-button">
                  <CL.Button size="m" color="light" onClick={handleOrderClick}>
                    {buttonText}
                  </CL.Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {showModal ? <Modal /> : null}
      <div className="of-subscription-card-container">
        <div className={`of-subscription-card-content ${dsClass.BORDER_WIDTH_1} ${dsClass.BORDER_STYLE_SOLID}`}>
          <div className="of-subscription-card-content-badges">{allBadges}</div>
          <div className={`of-subscription-card-content-flex-container ${dsClass.PADDING_BOTTOM_2}`}>
            <div className="of-subscription-card-content-flex-item of-main-content">
              <div className="of-subscription-card-content-flex-container">
                <div className="of-subscription-card-content-flex-item of-main-text-content">
                  <div className={`of-subscription-card-content-header ${marginByBadgesLength()}`}>
                    <span>{subHeader}</span>
                    <h3 className={`${dsClass.MARGIN_TOP_1} ${dsClass.MARGIN_BOTTOM_2}`}>{header}</h3>
                  </div>
                  <div className="of-subscription-card-content-description">
                    <span className={dsClass.TEXT_M}>{description}</span>
                  </div>
                  {showAlternateProductChoice && (
                    <div className="of-subscription-card-content-flex-item ds-margin-vertical--3">
                      <div className="of-subscription-card-content-header">
                        <h5 className="ds-margin-top--2 ds-margin-bottom--0 ds-padding-bottom--0">
                          {t.N1X4('One additional feature of your choice is included in the subscription price')}
                        </h5>
                        <div className="ds-text--s">
                          <CL.List>
                            {productVariantInfoBullets?.map((item, i) => <li key={`infoitem-${i}`}>{item.text}</li>)}
                          </CL.List>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div
                  className={`of-subscription-card-content-flex-item of-main-list-content ${isNewVoiceSubscription ? 'ds-align-content--flex-start' : ''}`}
                >
                  {isNewVoiceSubscription && (
                    <div className="of-subscription-card-content-header">
                      <span>&nbsp;</span>
                      <h5 className={`${dsClass.MARGIN_TOP_2} ${dsClass.MARGIN_BOTTOM_1} ${dsClass.PADDING_BOTTOM_0}`}>
                        {t.N1Z3('Subscription includes')}:
                      </h5>
                    </div>
                  )}
                  <div className="of-subscription-card-content-info-list ds-margin-left--3">
                    <InfoItemsList />
                  </div>
                </div>
              </div>
              {isMobileView && <Accordion />}
            </div>

            {!compact && showAlternateProductChoice ? (
              <div className="product-variant-container">
                <ProductVariantSelection subscriptionCard={subscriptionCard} onSelectOffer={onSelectOffer} />
                <CardPriceAndAction />
              </div>
            ) : (
              <CardPriceAndAction />
            )}
          </div>
          {!isMobileView && <Accordion />}
        </div>
      </div>
    </>
  );
};
